import React from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { formatTime12, DAY_ORDER } from "@/shared/lib/format";

const DAYS = DAY_ORDER;

/**
 * Per-day schedule preview.
 * Props:
 *   days: selected day codes
 *   pickup_time / return_time: defaults
 *   pickup_address / destination_address
 *   dayOverrides: { Mon: {arrival_time, return_time}, ... }
 *   buffer: travel buffer in minutes (for computing pickup from arrival override)
 */
function subMinutes(hhmm, m) {
  if (!hhmm) return "";
  const [h, mm] = hhmm.split(":").map(Number);
  const d = new Date(2000, 0, 1, h, mm); d.setMinutes(d.getMinutes() - m);
  return d.toTimeString().slice(0, 5);
}

export default function ScheduleGrid({
  days,
  pickup_time,
  return_time,
  pickup_address,
  destination_address,
  dayOverrides = {},
  buffer = 30,
}) {
  const dayActive = new Set(days || []);
  const active = DAYS.filter((d) => dayActive.has(d));

  const timesFor = (d) => {
    const ov = dayOverrides[d] || {};
    // Outbound pickup: subtract buffer from arrival-at-work
    const p = ov.arrival_time ? subMinutes(ov.arrival_time, buffer) : pickup_time;
    // Return pickup (at destination): exactly the leave-work time (no buffer — we arrive when the shift ends)
    const r = ov.return_time || return_time;
    return { p, r, overridden: !!(ov.arrival_time || ov.return_time) };
  };

  return (
    <div data-testid="schedule-grid">
      {/* Mobile vertical list */}
      <div className="md:hidden border hairline bg-white divide-y divide-[#E5E1D8]">
        {active.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#515E7B]">Pick at least one day to see your schedule.</div>
        ) : (
          active.map((d) => {
            const { p, r, overridden } = timesFor(d);
            return (
              <div key={d} className="p-4" data-testid={`grid-day-${d.toLowerCase()}-mobile`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="label-xs text-[#0A1128]">{d}</div>
                  {overridden && <div className="text-[10px] text-[#ed6500] font-semibold">CUSTOM</div>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="border-l-2 border-[#ed6500] pl-3 py-2 bg-[#F9F7F4]">
                    <div className="flex items-center gap-1 text-[9px] tracking-[0.12em] font-semibold uppercase text-[#ed6500]">
                      <ArrowRight size={10} strokeWidth={2} /> Out
                    </div>
                    <div className="font-serif text-xl leading-none mt-1">{formatTime12(p)}</div>
                    <div className="text-[10px] text-[#515E7B] mt-1 line-clamp-1">→ {destination_address || "Destination"}</div>
                  </div>
                  <div className="border-l-2 border-[#5c97f7] pl-3 py-2 bg-[#F9F7F4]">
                    <div className="flex items-center gap-1 text-[9px] tracking-[0.12em] font-semibold uppercase text-[#5c97f7]">
                      <ArrowLeft size={10} strokeWidth={2} /> Return
                    </div>
                    <div className="font-serif text-xl leading-none mt-1">{formatTime12(r)}</div>
                    <div className="text-[10px] text-[#515E7B] mt-1 line-clamp-1">→ {pickup_address || "Home"}</div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Desktop 7-column grid */}
      <div className="hidden md:block border hairline bg-white">
        <div className="grid grid-cols-7 border-b hairline">
          {DAYS.map((d) => (
            <div
              key={d}
              className={`label-xs p-4 text-center border-r hairline last:border-r-0 ${
                dayActive.has(d) ? "bg-[#0A1128] text-[#F9F7F4]" : "text-[#515E7B]"
              }`}
              data-testid={`grid-day-${d.toLowerCase()}`}
            >
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {DAYS.map((d) => {
            const on = dayActive.has(d);
            const { p, r, overridden } = on ? timesFor(d) : { p: "", r: "", overridden: false };
            return (
              <div key={d} className="border-r hairline last:border-r-0 min-h-[170px] p-2 space-y-2 relative">
                {overridden && <div className="absolute top-1 right-1 text-[8px] font-bold text-[#ed6500] tracking-widest">◆</div>}
                {on ? (
                  <>
                    <div className="border-l-2 border-[#ed6500] pl-2 py-2 bg-[#F9F7F4]">
                      <div className="flex items-center gap-1 text-[9px] tracking-[0.12em] font-semibold uppercase text-[#ed6500]">
                        <ArrowRight size={10} strokeWidth={2} /> Out
                      </div>
                      <div className="font-serif text-xl leading-none mt-1">{formatTime12(p)}</div>
                      <div className="text-[10px] text-[#515E7B] mt-1 line-clamp-2">→ {destination_address || "Destination"}</div>
                    </div>
                    <div className="border-l-2 border-[#5c97f7] pl-2 py-2 bg-[#F9F7F4]">
                      <div className="flex items-center gap-1 text-[9px] tracking-[0.12em] font-semibold uppercase text-[#5c97f7]">
                        <ArrowLeft size={10} strokeWidth={2} /> Return
                      </div>
                      <div className="font-serif text-xl leading-none mt-1">{formatTime12(r)}</div>
                      <div className="text-[10px] text-[#515E7B] mt-1 line-clamp-2">→ {pickup_address || "Home"}</div>
                    </div>
                  </>
                ) : (
                  <div className="h-full flex items-center justify-center text-[11px] text-[#515E7B]/50">—</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
